import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const VerifyEmail = () => {
  const { currentUser, resendVerificationEmail, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if already verified
    if (currentUser?.emailVerified) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  const handleResendEmail = async () => {
    try {
      setError('');
      setMessage('');
      setLoading(true);
      await resendVerificationEmail();
      setMessage('Verification email sent! Check your inbox.');
    } catch (err) {
      setError('Failed to send verification email. Please try again in a few minutes.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    try {
      setIsChecking(true);
      setError('');
      setMessage('');
      
      // Reload the user to get updated emailVerified status
      await currentUser.reload();
      
      if (currentUser.emailVerified) {
        setMessage('Email verified! Redirecting to dashboard...');
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        setError('Email not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (err) {
      setError('Failed to check verification status. Please try again.');
    } finally {
      setIsChecking(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Failed to log out:', err);
    }
  };

  if (!currentUser) {
    navigate('/login');
    return null;
  }

  return (
    <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: '80vh' }}>
      <div className="w-100" style={{ maxWidth: '500px' }}>
        <Card>
          <Card.Body>
            <div className="text-center mb-4">
              <div className="mb-3">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="64" 
                  height="64" 
                  fill="currentColor" 
                  className="text-primary" 
                  viewBox="0 0 16 16"
                >
                  <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
                </svg>
              </div>
              <h2>Verify Your Email</h2>
            </div>

            {message && <Alert variant="success">{message}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
            
            <Alert variant="info">
              <p className="mb-2">
                <strong>We've sent a verification email to:</strong>
              </p>
              <p className="mb-2">
                <code>{currentUser?.email}</code>
              </p>
              <p className="mb-0 small">
                Please check your inbox and click the verification link to activate your account.
              </p>
            </Alert>

            <div className="d-grid gap-2 mb-3">
              <Button 
                variant="primary" 
                onClick={handleCheckVerification}
                disabled={isChecking}
              >
                {isChecking ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Checking...
                  </>
                ) : (
                  'I\'ve Verified My Email'
                )}
              </Button>

              <Button 
                variant="outline-secondary" 
                onClick={handleResendEmail}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Resend Verification Email'}
              </Button>
            </div>

            <hr />

            <div className="text-center">
              <p className="text-muted small mb-2">
                Don't see the email? Check your spam folder.
              </p>
              <Button variant="link" size="sm" onClick={handleLogout}>
                Sign out and use a different email
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default VerifyEmail;
